import React, { useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  updateProfile,
  sendEmailVerification
} from "firebase/auth";
import { doc, setDoc, serverTimestamp, getDocs, collection, query, where, addDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { AccountType, SystemSettings } from '../types';
import * as OTPAuth from 'otpauth';

interface AuthProps {
  onSuccess: () => void;
  onClose: () => void;
  systemSettings: SystemSettings | null;
}

const Auth: React.FC<AuthProps> = ({ onSuccess, onClose, systemSettings }) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'verify' | 'success' | '2fa'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [tempUser, setTempUser] = useState<any>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      localStorage.setItem('pendingReferral', ref.toUpperCase());
    } else {
      const storedRef = localStorage.getItem('pendingReferral');
      if (storedRef) setReferralCode(storedRef);
    }
  }, []);

  const sendEmailNotification = async (to: string, subject: string, text: string) => {
    try {
      const siteName = systemSettings?.siteName || 'BTCC-AI';
      
      await addDoc(collection(db, 'mail'), {
        to,
        message: {
          subject: `[${siteName}] ${subject}`,
          text,
          html: `
            <div style="background-color: #000; color: #fff; padding: 40px; font-family: sans-serif; border-radius: 20px;">
              <h2 style="color: #AEB877; text-transform: uppercase; letter-spacing: 2px;">${siteName} Systems</h2>
              <div style="border-left: 2px solid #AEB877; padding-left: 20px; margin: 20px 0;">
                <p style="font-size: 16px; line-height: 1.6;">${text}</p>
              </div>
              <p style="font-size: 10px; color: #444; text-transform: uppercase; letter-spacing: 1px;">This is an automated neural transmission. Do not reply.</p>
            </div>
          `
        }
      });
    } catch (err) {
      console.error("Mail queue error:", err);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!tempUser || !tempUser.twoFactorSecret) {
        setError('Security context lost. Please restart login.');
        setMode('login');
        return;
      }

      const secret = tempUser.twoFactorSecret;
      const totp = new OTPAuth.TOTP({
        secret: OTPAuth.Secret.fromBase32(secret),
        digits: 6,
        period: 30
      });

      const delta = totp.validate({ token: twoFactorCode, window: 1 });
      if (delta !== null) {
        onSuccess();
      } else {
        setError('Invalid security code. Please try again.');
      }
    } catch (err: any) {
      setError('Verification failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Check for 2FA
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().is2faEnabled) {
          setTempUser(userDoc.data());
          setMode('2fa');
          setLoading(false);
          return;
        }

        onSuccess();
      } else if (mode === 'signup') {
        let referrerId: string | undefined = undefined;
        let referrerEmail: string | undefined = undefined;
        let referrerName: string | undefined = undefined;
        
        const refToUse = referralCode || localStorage.getItem('pendingReferral');
        if (refToUse) {
          const q = query(collection(db, 'users'), where('referralCode', '==', refToUse));
          const snap = await getDocs(q);
          if (!snap.empty) {
            referrerId = snap.docs[0].id;
            referrerEmail = snap.docs[0].data().email;
            referrerName = snap.docs[0].data().fullName;
          }
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        const fullName = `${firstName} ${lastName}`;
        await updateProfile(user, { displayName: fullName });
        
        const myRefCode = user.uid.slice(0, 8).toUpperCase();

        await setDoc(doc(db, 'users', user.uid), {
          fullName: fullName,
          firstName: firstName,
          lastName: lastName,
          username: username,
          email: user.email || email,
          demoBalance: 10000.00,
          liveBalance: 0.00,
          createdAt: serverTimestamp(),
          registrationDate: new Date().toISOString(),
          uid: user.uid,
          isVerified: false,
          botActive: false,
          accountType: AccountType.DEMO,
          role: 'user',
          status: 'active',
          referralCode: myRefCode,
          referredBy: referrerId || null,
          country: country,
          phoneNumber: phone
        });

        // Send Welcome Email
        sendEmailNotification(
          email,
          `Welcome to ${systemSettings?.siteName || 'BTCC-AI'}`,
          `Greetings ${firstName}, your neural trading node has been initialized. Please verify your email to commence operations.`
        );

        // Notify Referrer
        if (referrerEmail) {
          sendEmailNotification(
            referrerEmail,
            'New Node Connection',
            `Greetings ${referrerName}, a new user has initialized their node using your referral identifier. You will receive yields on their future live executions.`
          );
        }

        localStorage.removeItem('pendingReferral');
        await sendEmailVerification(user);
        onSuccess();
      }
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        setError('User already exists. Please sign in');
      } else if (
        err.code === 'auth/invalid-credential' || 
        err.code === 'auth/wrong-password' || 
        err.code === 'auth/user-not-found'
      ) {
        setError('Invalid email or password. Please check your credentials.');
      } else {
        setError(err.message || 'An authentication error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'success') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl">
        <div className="w-full max-w-md bg-surface border border-border rounded-[40px] p-10 text-center animate-in zoom-in-95 duration-300">
          <div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-[0_0_30px_var(--color-primary-glow)]">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="4"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </div>
          <h2 className="text-3xl font-black uppercase tracking-tighter mb-4">Congratulations!</h2>
          <p className="text-muted text-xs font-bold uppercase tracking-widest leading-relaxed mb-10">Your account has been verified successfully</p>
          <button onClick={onSuccess} className="w-full primary-button py-5">OKAY</button>
        </div>
      </div>
    );
  }

  if (mode === '2fa') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl">
        <div className="w-full max-w-md bg-surface border border-border rounded-[40px] p-10 relative shadow-2xl animate-in zoom-in-95 duration-300">
          <button 
            onClick={onClose}
            className="absolute top-8 right-8 text-muted hover:text-white transition-colors p-2 z-20"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>

          <div className="mb-10 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-primary/20">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#AEB877" strokeWidth="3"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </div>
            <h2 className="text-3xl font-black uppercase tracking-tighter text-white">Security Check</h2>
            <p className="text-muted text-[10px] font-black uppercase tracking-[0.3em] mt-3">Enter the 6-digit code from your authenticator app</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-500 text-[10px] font-black uppercase tracking-widest text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleVerify2FA} className="space-y-6">
            <input
              type="text"
              required
              maxLength={6}
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
              className="w-full bg-black/40 border border-white/10 rounded-[24px] py-6 text-center text-white text-4xl font-black tracking-[0.5em] outline-none focus:border-primary transition-all"
              placeholder="000000"
              autoFocus
            />

            <button
              type="submit"
              disabled={loading || twoFactorCode.length !== 6}
              className="w-full primary-button py-5"
            >
              {loading ? 'Verifying...' : 'Authorize Access'}
            </button>

            <button 
              type="button"
              onClick={() => setMode('login')}
              className="w-full text-muted text-[10px] font-black uppercase tracking-widest hover:text-white transition-colors"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl overflow-y-auto no-scrollbar">
      <div className="w-full max-w-md bg-surface border border-border rounded-[40px] overflow-hidden relative shadow-2xl animate-in zoom-in-95 duration-300 max-h-[95vh] flex flex-col">
        <button 
          onClick={onClose}
          className="absolute top-8 right-8 text-muted hover:text-white transition-colors p-2 z-20"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>

        <div className="flex-1 overflow-y-auto no-scrollbar p-10">
          <div className="mb-10 text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <span className="text-black font-black text-2xl">{systemSettings?.siteName?.[0] || 'B'}</span>
              </div>
              <span className="font-black text-2xl tracking-tighter text-white uppercase">{systemSettings?.siteName || 'BTCC-AI'}</span>
            </div>
            <h2 className="text-3xl font-black uppercase tracking-tighter text-white">
              {mode === 'login' ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-muted text-[10px] font-black uppercase tracking-[0.3em] mt-3">
              {mode === 'login' ? 'Sign in to your account' : `Create your account to access ${systemSettings?.siteName || 'BTCC-AI'}`}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-500 text-[10px] font-black uppercase tracking-widest text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'signup' ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[9px] font-black text-muted uppercase tracking-widest mb-2 ml-1">First Name</label>
                    <input type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full input-field" placeholder="Enter" />
                  </div>
                  <div>
                    <label className="block text-[9px] font-black text-muted uppercase tracking-widest mb-2 ml-1">Last Name</label>
                    <input type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full input-field" placeholder="Enter" />
                  </div>
                </div>
                <div>
                  <label className="block text-[9px] font-black text-muted uppercase tracking-widest mb-2 ml-1">Username</label>
                  <input type="text" required value={username} onChange={(e) => setUsername(e.target.value)} className="w-full input-field" placeholder="Enter username" />
                </div>
                <div>
                  <label className="block text-[9px] font-black text-muted uppercase tracking-widest mb-2 ml-1">Email Address</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full input-field" placeholder="Enter email address" />
                </div>
                <div>
                  <label className="block text-[9px] font-black text-muted uppercase tracking-widest mb-2 ml-1">Country</label>
                  <input type="text" required value={country} onChange={(e) => setCountry(e.target.value)} className="w-full input-field" placeholder="Enter country" />
                </div>
                <div>
                  <label className="block text-[9px] font-black text-muted uppercase tracking-widest mb-2 ml-1">Phone Number</label>
                  <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full input-field" placeholder="Enter phone number" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-[9px] font-black text-muted uppercase tracking-widest mb-2 ml-1">Email Address</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full input-field" placeholder="Enter email address" />
                </div>
              </>
            )}
            
            <div>
              <div className="flex justify-between items-center mb-2 ml-1">
                <label className="block text-[9px] font-black text-muted uppercase tracking-widest">Password</label>
                {mode === 'login' && (
                  <button type="button" className="text-[9px] font-black text-primary uppercase tracking-widest hover:underline">Forgot Password?</button>
                )}
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full input-field"
                placeholder="Enter password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full primary-button py-5 mt-4"
            >
              {loading ? 'Transmitting...' : (mode === 'login' ? 'SIGN IN' : 'SIGN UP')}
            </button>
          </form>

          <div className="mt-10 pt-10 border-t border-border text-center">
            <p className="text-muted text-[10px] font-black uppercase tracking-widest">
              {mode === 'login' ? "Don't have an account ?" : "Already have an account ?"} {' '}
              <button 
                onClick={() => setMode(mode === 'login' ? 'signup' : 'login')} 
                className="text-primary hover:underline ml-1"
              >
                {mode === 'login' ? 'Create Account' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;